import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { assetDownloadPattern, internalObjectPattern, internalSurveyPattern, isAllowedImageSrc } from './chatUrlAllowlist';

// Strict-allowlist markdown renderer. Defense-in-depth against LLM
// off-script: only /objects/<uuid>, /surveys/<uuid>, /api/assets/<uuid>
// URLs become links (or <img>); anything else renders as plain text.

interface IChatMarkdownProps {
    content: string;
}

export function ChatMarkdown({ content }: IChatMarkdownProps): JSX.Element {
    return (
        <div className="chat-markdown">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                // Explicit allowlist — defends against future AST node types.
                allowedElements={[
                    'p',
                    'strong',
                    'em',
                    'ul',
                    'ol',
                    'li',
                    'a',
                    'img',
                    'code',
                    'pre',
                    'blockquote',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'table',
                    'thead',
                    'tbody',
                    'tr',
                    'th',
                    'td',
                    'br',
                    'hr',
                ]}
                components={{
                    a: ({ href, children }) => {
                        if (!href) {
                            return <>{children}</>;
                        }
                        if (internalObjectPattern.test(href) || internalSurveyPattern.test(href)) {
                            // SPA nav so Redux state survives.
                            return <Link to={href}>{children}</Link>;
                        }
                        if (assetDownloadPattern.test(href)) {
                            // New tab so the chat panel doesn't lose state.
                            return (
                                <a href={href} target="_blank" rel="noopener noreferrer">
                                    {children}
                                </a>
                            );
                        }
                        // Untrusted — render as text.
                        return <span>{children}</span>;
                    },
                    img: ({ src, alt }) => {
                        if (!src || !isAllowedImageSrc(src)) {
                            return null;
                        }
                        return (
                            <a href={src.replace(/[?&]thumb=true/u, '')} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={src}
                                    alt={alt ?? ''}
                                    loading="lazy"
                                    style={{ maxWidth: 200, maxHeight: 200, borderRadius: 4, margin: '4px 0' }}
                                />
                            </a>
                        );
                    },
                    // Bootstrap table classes for consistency with the rest of the app.
                    table: ({ children }) => <table className="table table-condensed table-striped">{children}</table>,
                    // The global Bootstrap blockquote scales to 125% of base —
                    // that visually shouts inside a compact chat bubble. Override
                    // to inherit the message's own font-size and tighten padding.
                    blockquote: ({ children }) => (
                        <blockquote style={{ fontSize: 'inherit', padding: '6px 12px', margin: '6px 0', borderLeftWidth: 3 }}>{children}</blockquote>
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}

// Re-exported via chatUrlAllowlist.ts; tests import from there.
